import React from 'react';
import { ListGroup, Badge, Spinner } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSearch, faEnvelope, faBullhorn, faBell } from '@fortawesome/free-solid-svg-icons';
import UserAvatar from './UserAvatar';
import { formatTimestamp } from '../../utils/dateUtils';
import './SearchResults.css';

/**
 * SearchResults Component
 * 
 * Displays search results with highlighting and metadata.
 * 
 * Features:
 * - Result highlighting for search terms
 * - Message type indicators
 * - Sender information
 * - Timestamp display
 * - Click to navigate to thread
 * 
 * Requirements: 5.2, 5.5
 */
function SearchResults({ results, searchQuery, loading, onSelectThread, filters }) {
  /**
   * Highlight search terms in text
   * Requirement 5.5: Highlight search terms in message results
   */
  const highlightText = (text, query) => {
    if (!query || !text) return text;

    const parts = text.split(new RegExp(`(${query})`, 'gi'));
    return parts.map((part, index) => 
      part.toLowerCase() === query.toLowerCase() ? (
        <mark key={index} className="search-highlight">{part}</mark>
      ) : (
        part
      )
    );
  };

  const getMessageTypeIcon = (type) => {
    switch (type) {
      case 'notification':
        return faBell;
      case 'announcement':
        return faBullhorn;
      default:
        return faEnvelope;
    }
  };

  const getMessageTypeBadge = (type) => {
    switch (type) {
      case 'notification':
        return <Badge bg="info" className="ms-2">Notification</Badge>;
      case 'announcement':
        return <Badge bg="warning" className="ms-2">Announcement</Badge>;
      default:
        return null;
    }
  };

  const handleResultClick = (result) => {
    // Navigate to the thread
    if (result.threadId) {
      onSelectThread({ threadId: result.threadId });
    }
  };

  if (loading) {
    return (
      <div className="text-center py-5">
        <Spinner animation="border" variant="primary" />
        <p className="mt-2 text-muted">Searching...</p>
      </div>
    );
  }

  if (!searchQuery && Object.keys(filters).length === 0) {
    return (
      <div className="text-center py-5 text-muted">
        <FontAwesomeIcon icon={faSearch} size="3x" className="mb-3 opacity-25" />
        <p>Enter a search query to find messages</p>
        <p className="small">You can search by message content, sender, or use filters</p>
      </div>
    );
  }

  if (results.length === 0) {
    return (
      <div className="text-center py-5 text-muted">
        <FontAwesomeIcon icon={faSearch} size="3x" className="mb-3 opacity-25" />
        <p>No messages found</p>
        <p className="small">Try adjusting your search query or filters</p>
      </div>
    );
  }

  return (
    <div className="search-results">
      <div className="results-header mb-3">
        <span className="text-muted">
          Found {results.length} {results.length === 1 ? 'result' : 'results'}
        </span>
      </div>

      <ListGroup variant="flush">
        {results.map((result, index) => {
          // Handle both message and conversation result formats
          const message = result.lastMessage || result;
          const threadId = result.threadId || message.threadId;
          const messageType = result.messageType || message.messageType || 'text';
          const content = message.content || '';
          const senderName = message.senderName || result.participantNames?.[0] || 'Unknown';
          const senderPhotoUrl = message.senderPhotoUrl || result.participantPhotos?.[0];
          const timestamp = message.timestamp || message.createdAt || result.updatedAt;

          return (
            <ListGroup.Item
              key={index}
              action
              onClick={() => handleResultClick(result)}
              className="search-result-item"
            >
              <div className="d-flex align-items-start">
                {/* Sender Avatar */}
                <UserAvatar
                  photoUrl={senderPhotoUrl}
                  displayName={senderName}
                  size="md"
                  className="me-3"
                />

                {/* Message Content */}
                <div className="flex-grow-1">
                  <div className="d-flex justify-content-between align-items-start mb-1">
                    <div className="result-sender">
                      <strong>{senderName}</strong>
                      {getMessageTypeBadge(messageType)}
                    </div>
                    <div className="result-timestamp text-muted small">
                      {formatTimestamp(timestamp)}
                    </div>
                  </div>

                  <div className="result-content">
                    {highlightText(content, searchQuery)}
                  </div>

                  {/* Thread Info */}
                  {result.participantNames && result.participantNames.length > 1 && (
                    <div className="result-thread-info text-muted small mt-1">
                      <FontAwesomeIcon icon={faEnvelope} className="me-1" />
                      Conversation with {result.participantNames.slice(0, 3).join(', ')}
                      {result.participantNames.length > 3 && ` and ${result.participantNames.length - 3} more`}
                    </div>
                  )}

                  {/* Message Count for Conversations */}
                  {result.messageCount && (
                    <div className="result-metadata text-muted small mt-1">
                      {result.messageCount} {result.messageCount === 1 ? 'message' : 'messages'}
                    </div>
                  )}
                </div>

                {/* Message Type Icon */}
                <div className="result-type-icon ms-2">
                  <FontAwesomeIcon
                    icon={getMessageTypeIcon(messageType)}
                    className="text-muted"
                  />
                </div>
              </div>
            </ListGroup.Item>
          );
        })}
      </ListGroup>
    </div>
  );
}

export default SearchResults;
